/**
 * FSSModerationPanel — Admin moderation tab to delete entries from the
 * Franchises / Studios / Streaming catalog. Same backend as the CRUD panel.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Trash2, RefreshCw, Building2, Tv, Sparkles, Filter } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_ICON = { franchise: Sparkles, studio: Building2, streaming: Tv };

const FSSModerationPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filterType ? `/api/admin/franchises?type=${filterType}` : '/api/admin/franchises';
      const r = await axios.get(url);
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id, title) => {
    if (!window.confirm(`Supprimer définitivement « ${title} » du catalogue FSS ?`)) return;
    try {
      await axios.delete(`/api/admin/franchises/${id}`);
      toast.success('Supprimé');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="space-y-3" data-testid="fss-mod-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-purple-300" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-white/10 border border-white/15 rounded-lg px-2 py-1 text-xs text-white"
            data-testid="fss-mod-filter"
          >
            <option value="">Tous types</option>
            <option value="franchise">Franchises</option>
            <option value="studio">Studios</option>
            <option value="streaming">Streaming</option>
          </select>
        </div>
        <button onClick={load} disabled={loading} className="text-xs px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white flex items-center gap-1.5">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Recharger
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-white/40 text-sm">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-white/40 text-sm" data-testid="fss-mod-empty">Aucun élément</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {items.map(it => {
            const Icon = TYPE_ICON[it.type] || Tv;
            return (
              <div key={it.id} className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-3" data-testid={`fss-mod-${it.id}`}>
                <div className="w-12 h-12 bg-zinc-900 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                  {it.image_url ? <img src={it.image_url} alt="" className="max-w-full max-h-full object-contain" /> : <Icon className="w-5 h-5 text-white/30" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/40">
                    <Icon className="w-3 h-3" /> {it.type}
                  </div>
                  <div className="text-white font-semibold truncate">{it.title}</div>
                  {it.price_label && <div className="text-[11px] text-emerald-300">{it.price_label}</div>}
                </div>
                <button
                  onClick={() => remove(it.id, it.title)}
                  className="p-2 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 flex-shrink-0"
                  data-testid={`fss-mod-delete-${it.id}`}
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FSSModerationPanel;
